import { Book } from '../types';
import { featuredBooks, getBooksByGenre, getAllBooks } from '../data/books';

interface QuizResults {
  [key: string]: string;
}

const MIN_RECOMMENDATIONS = 3;
const MAX_RECOMMENDATIONS = 5;

// Keep track of shown recommendations per session
const shownRecommendations = new Set<string>();

export async function getRecommendations(quizResults: QuizResults): Promise<Book[]> {
  try {
    if (!quizResults || typeof quizResults !== 'object') {
      return [featuredBooks.weeklyBook];
    }

    let books: Book[] = [];
    
    // Get books from primary genre
    if (quizResults.primary_genre) {
      const genreBooks = await getBooksByGenre(quizResults.primary_genre);
      if (genreBooks.length > 0) {
        books.push(...genreBooks);
      }
    }

    // Get books from secondary genre if specified
    if (quizResults.secondary_genre) {
      const secondaryBooks = await getBooksByGenre(quizResults.secondary_genre);
      books.push(...secondaryBooks);
    }

    // If we don't have enough books, get books from all genres
    if (books.length < MIN_RECOMMENDATIONS) {
      const allBooks = await getAllBooks();
      books.push(...allBooks);
    }

    // Filter out already shown books and ensure valid books
    const validBooks = books.filter(book => 
      book && 
      book.title && 
      book.author && 
      book.summary && 
      book.coverUrl &&
      !shownRecommendations.has(`${book.title}-${book.author}`)
    );

    if (validBooks.length === 0) {
      return [featuredBooks.weeklyBook];
    }

    // Rank and select recommendations
    const recommendations = rankBooks(validBooks, quizResults)
      .slice(0, MAX_RECOMMENDATIONS);

    // Add to shown recommendations
    recommendations.forEach(book => {
      shownRecommendations.add(`${book.title}-${book.author}`);
    });

    return recommendations;
  } catch (error) {
    console.error('Error getting recommendations:', error);
    return [featuredBooks.weeklyBook];
  }
}

function rankBooks(books: Book[], results: QuizResults): Book[] {
  return books
    .filter(book => book && book.title && book.author && book.summary)
    .sort((a, b) => {
      const scoreA = calculateMatchScore(a, results);
      const scoreB = calculateMatchScore(b, results);
      return scoreB - scoreA;
    });
}

function calculateMatchScore(book: Book, results: QuizResults): number {
  if (!book || !book.genre || !book.mood) return 0;

  let score = 0;
  const genres = book.genre.map(g => g.toLowerCase());
  const moods = book.mood.map(m => m.toLowerCase());

  // Primary genre match (highest weight)
  if (results.primary_genre) {
    const primaryGenre = results.primary_genre.toLowerCase();
    if (genres.includes(primaryGenre)) score += 10;
    else if (genres.some(g => g.includes(primaryGenre))) score += 5;
  }

  // Secondary genre match
  if (results.secondary_genre) {
    const secondaryGenre = results.secondary_genre.toLowerCase();
    if (genres.includes(secondaryGenre)) score += 7;
    else if (genres.some(g => g.includes(secondaryGenre))) score += 3;
  }

  // Reading mood match
  if (results.reading_mood) {
    const mood = results.reading_mood.toLowerCase();
    if (book.summary.toLowerCase().includes(mood)) score += 5;
  }

  // Length preference match
  if (results.length && book.length === results.length) {
    score += 4;
  }

  // Emotional tone match
  if (results.emotional_tone) {
    const tone = results.emotional_tone.toLowerCase();
    if (moods.includes(tone)) score += 6;
    else if (moods.some(m => m.includes(tone))) score += 3;
  }

  // Theme match
  if (results.themes && book.summary) {
    const theme = results.themes.toLowerCase();
    if (book.summary.toLowerCase().includes(theme)) score += 5;
  }

  // Content rating match
  if (results.content_rating) {
    const rating = results.content_rating.toLowerCase();
    if (book.summary.toLowerCase().includes(rating)) score += 3;
  }

  // Add small random factor for variety (0-1)
  score += Math.random();

  return score;
}